import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, CalendarClock, FlaskConical, Mail, Loader2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/lib/site";
import { sendExitLead } from "@/lib/api";
import { money } from "@/lib/pricing";

const KEY = "fixlr_exit_intent_shown";

const readPlan = () => { try { return JSON.parse(sessionStorage.getItem("fixlr_plan") || "null"); } catch { return null; } };

export const ExitIntent = ({ page = "/pricing", delayMs = 4000 }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const armed = useRef(false);
  const lastY = useRef(0);
  const plan = readPlan();

  const show = useCallback(() => {
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, "1");
    setOpen(true);
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return undefined;
    const arm = setTimeout(() => { armed.current = true; }, delayMs);
    const onLeave = (e) => { if (armed.current && e.clientY <= 0 && !e.relatedTarget) show(); };
    const onScroll = () => {
      const y = window.scrollY;
      const progressed = y > window.innerHeight * 0.6;
      if (armed.current && progressed && lastY.current - y > 140) show();
      lastY.current = y;
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mouseout", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(arm); document.removeEventListener("mouseout", onLeave); window.removeEventListener("scroll", onScroll); window.removeEventListener("keydown", onKey); };
  }, [delayMs, show]);

  // Test hook: ?exit=1 forces the prompt (handy for QA); never shown twice per session.
  useEffect(() => { if (new URLSearchParams(window.location.search).get("exit") === "1") { sessionStorage.removeItem(KEY); setTimeout(show, 600); } }, [show]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid work email"); return; }
    setBusy(true);
    try {
      await sendExitLead({ email, intent: "pricing_breakdown", plan: plan || undefined, page });
      setDone(true);
      toast.success("Sent — check your inbox");
    } catch { toast.error("Couldn't send just now — try again"); }
    finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setOpen(false)} data-testid="exit-intent-backdrop">
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl rounded-3xl glass p-6 sm:p-8 glow-lime" role="dialog" aria-modal="true" aria-labelledby="exit-title" data-testid="exit-intent-modal">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full border border-mk-border text-mk-dim hover:text-mk-text hover:border-mk-strong transition-colors" aria-label="Close" data-testid="exit-intent-close"><X className="h-4 w-4" /></button>
            <div className="eyebrow">Before you go</div>
            <h2 id="exit-title" className="mt-2 font-display text-2xl sm:text-3xl font-bold text-mk-text">Pricing is the easy part. <span className="text-mk-lime">Seeing it on your jobs</span> is the point.</h2>
            <p className="mt-2 text-sm text-mk-dim">Two zero-risk ways to see Fixlr working on your properties and suppliers — pick whichever fits your week.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <Link to="/contact" onClick={() => setOpen(false)} className="group rounded-2xl border border-mk-lime/40 bg-mk-lime/10 p-5 hover:bg-mk-lime/15 transition-colors" data-testid="exit-intent-demo">
                <CalendarClock className="h-6 w-6 text-mk-lime" />
                <div className="mt-3 font-display font-semibold text-mk-text">20-minute demo</div>
                <p className="mt-1 text-xs text-mk-dim leading-relaxed">We set it up live with your data on the call. No slides.</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-mk-lime">Book a slot <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </Link>
              <a href={SITE.signupUrl} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="group rounded-2xl border border-mk-border bg-mk-surface p-5 hover:border-mk-strong transition-colors" data-testid="exit-intent-sandbox">
                <FlaskConical className="h-6 w-6 text-[#7fb0ff]" />
                <div className="mt-3 font-display font-semibold text-mk-text">Free sandbox</div>
                <p className="mt-1 text-xs text-mk-dim leading-relaxed">Pre-loaded with jobs, suppliers and a client portal. No card.</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-mk-text">Open sandbox <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </a>
            </div>

            <div className="mt-5 rounded-2xl border border-mk-border bg-mk-bg/60 p-4">
              {done ? (
                <div className="flex items-center gap-2 text-sm text-mk-lime" data-testid="exit-intent-sent"><Check className="h-4 w-4" />Breakdown sent to {email}. We'll only follow up if you reply.</div>
              ) : (
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label htmlFor="exit-email" className="text-xs text-mk-dim flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Or email me the breakdown{plan?.monthly != null && <span className="text-mk-muted">· {plan.preset_name}, {plan.seats} seat{plan.seats === 1 ? "" : "s"} → <span className="text-mk-text font-semibold">{money(plan.monthly)}/mo</span></span>}</label>
                    <input id="exit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="input-dark mt-1.5 w-full" data-testid="exit-intent-email-input" />
                  </div>
                  <button type="submit" disabled={busy} className="btn-ghost sm:self-end whitespace-nowrap" data-testid="exit-intent-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send it"}</button>
                </form>
              )}
            </div>
            <div className="mt-3 text-[11px] text-mk-muted">No spam, ever. One email with the numbers and both links.</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntent;
